import React from 'react';
import { Flex, Space, Typography } from 'antd';
import { PlusOutlined, StopOutlined, SwapOutlined } from '@ant-design/icons';
import Balance from '../Balance/Balance';
import CardsBlock from '../Cards/Cards';
import OtherBlock from '../Other/Other';
import classes from './AccountLayout.module.css';
import { useIntl } from 'react-intl';
import { Card, CStatus } from 'entities/card/model/types';
import { ActionButton, BackButton, GridContainer } from 'shared/ui';
import { AccStatus } from 'entities/account/model/types';
import Transactions from '../../../../widgets/transactions/ui/Transactions';
import { ChangeAccountName } from 'features/account/change-account-name';
import { GetCards } from '../GetCards/GetCards';

type AccountLayoutProps = {
  color: string;
  title: string;
  balance: number;
  card?: Card;
  iban: string;
  status?: string | null;
  canAddCard?: boolean;
  onTopup: () => unknown;
  onMoveFunds?: () => unknown;
  showMoveFunds?: boolean;
  onStatementDownload?: ({
    dateRange,
    format,
    id
  }: {
    dateRange: Array<string>;
    format: 'pdf' | 'csv';
    id?: number | string;
  }) => Promise<any> | void;
};

const AccountLayout: React.FC<AccountLayoutProps> = ({
  title,
  color,
  balance = 0,
  status,
  card,
  canAddCard = false,
  onTopup,
  onMoveFunds,
  showMoveFunds = false,
  onStatementDownload
}) => {
  const { formatMessage: t } = useIntl();

  return (
    <>
      <GridContainer
        rowProps={{
          style: {
            background: color,
            paddingBottom: 32
          }
        }}
      >
        <BackButton />
        <Space className={classes.fullWidth} direction='vertical' size={24}>
          <Space className={classes.fullWidth} direction='vertical' size={4}>
            {status === AccStatus.Closed && (
              <Flex gap={8} align='center' style={{ marginBottom: 20 }}>
                <StopOutlined style={{ fontSize: 16 }} />
                <Typography.Text style={{ fontWeight: 600, fontSize: 18 }}>
                  {t({ id: 'river.views.accountStatusPage.closed.title' })}
                </Typography.Text>
              </Flex>
            )}
            <Space size={8}>
              <Typography.Text className={classes.title}>{title}</Typography.Text>
              <ChangeAccountName />
            </Space>
            <Balance balance={balance} />
          </Space>
          <Flex gap={8}>
            <ActionButton
              text={t({ id: 'buttons.topup' })}
              icon={<PlusOutlined style={{ fontSize: 24 }} />}
              onClick={onTopup}
              id='btn__load'
            />
            {showMoveFunds && onMoveFunds && (
              <ActionButton
                text={t({ id: 'buttons.moveFunds', defaultMessage: 'Move funds' })}
                icon={<SwapOutlined style={{ fontSize: 24 }} />}
                onClick={onMoveFunds}
                id='btn__move_funds'
              />
            )}
          </Flex>
        </Space>
      </GridContainer>
      <GridContainer
        rowProps={{
          style: {
            marginTop: 32
          }
        }}
      >
        <Space className={classes.fullWidth} direction='vertical' size={20}>
          {canAddCard && <GetCards />}
          <Transactions />
          {card && card.status !== CStatus.Closed && <CardsBlock card={card} />}
          <OtherBlock onStatementDownload={onStatementDownload} />
        </Space>
      </GridContainer>
    </>
  );
};

export default AccountLayout;
